import React from 'react';
import { Modal, Form, Input, message, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { addBank, updateBank } from '../../services/bankService';
import { useStyles } from '../../theme/hooks';
import { LoadScript, Autocomplete } from '@react-google-maps/api';

const GOOGLE_MAPS_LIBRARIES = ['places'];

const useBankFormStyles = () => useStyles((theme) => ({
    form: {
        maxWidth: '100%'
    },
    section: {
        marginBottom: theme.spacing.lg
    },
    autocompleteInput: {
        width: '100%',
        height: '32px',
        padding: '4px 11px',
        borderRadius: '6px',
        border: '1px solid #d9d9d9',
        '&:hover': {
            borderColor: '#4096ff',
        },
        '&:focus': {
            borderColor: '#4096ff',
            boxShadow: '0 0 0 2px rgba(5, 145, 255, 0.1)',
            outline: 'none',
        }
    }
}));

const BankForm = ({ visible, onCancel, onSuccess, initialValues }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const styles = useBankFormStyles();
    const [autocomplete, setAutocomplete] = React.useState(null);
    const isEdit = !!initialValues;

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const apiCall = isEdit ? updateBank : addBank;
            
            // Ensure all fields are included in the request
            const formData = {
                name: values.name || '',
                contact_person: values.contact_person || null,
                street: values.street || null,
                zip_code: values.zip_code || null,
                city: values.city || null,
                email: values.email || null,
                phone: values.phone || null,
                additional_info: values.additional_info || '',
                ...(isEdit && { id: initialValues.id }),
            };
            
            await apiCall(formData);
            message.success(t(isEdit ? 'bank.messages.updateSuccess' : 'bank.messages.createSuccess'));
            form.resetFields();
            onSuccess();
        } catch (error) {
            console.error('Error saving bank:', error);
            message.error(t(isEdit ? 'bank.messages.updateError' : 'bank.messages.createError'));
        }
    };

    const onPlaceSelected = () => {
        if (autocomplete) {
            const place = autocomplete.getPlace();
            
            let streetName = '';
            let postalCode = '';
            let city = '';

            // Extract address components
            place.address_components.forEach(component => {
                const types = component.types;
                
                if (types.includes('route')) {
                    streetName = component.long_name;
                }
                if (types.includes('postal_code')) {
                    postalCode = component.long_name;
                }
                if (types.includes('locality')) {
                    city = component.long_name;
                }
            });

            // Update form fields
            form.setFieldsValue({
                street: streetName,
                zip_code: postalCode,
                city: city
            });
        }
    };

    return (
        <Modal
            open={visible}
            title={t(isEdit ? 'bank.modal.editTitle' : 'bank.modal.title')}
            onCancel={onCancel}
            onOk={handleSubmit}
            width={800}
        >
            <LoadScript
                googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                libraries={GOOGLE_MAPS_LIBRARIES}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={initialValues}
                    style={styles.form}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label={t('bank.fields.name')}
                                rules={[{ required: true, message: t('common.validation.required') }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="contact_person"
                                label={t('bank.fields.contactPerson')}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="email"
                                label={t('bank.fields.email')}
                                rules={[
                                    { type: 'email', message: t('common.validation.invalidEmail') }
                                ]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="phone"
                                label={t('bank.fields.phone')}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="additional_info"
                                label={t('bank.fields.additionalInfo')}
                            >
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label={t('customer.fields.addressSearch')}
                            >
                                <Autocomplete
                                    onLoad={setAutocomplete}
                                    onPlaceChanged={onPlaceSelected}
                                    restrictions={{ country: ['de', 'at', 'ch', 'nl', 'be', 'lu', 'dk', 'pl', 'cz'] }}
                                >
                                    <input
                                        type="text"
                                        placeholder={t('customer.fields.searchPlaceholder')}
                                        style={styles.autocompleteInput}
                                    />
                                </Autocomplete>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="street"
                                label={t('bank.fields.street')}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="zip_code"
                                label={t('bank.fields.zipCode')}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={16}>
                            <Form.Item
                                name="city"
                                label={t('bank.fields.city')}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </LoadScript>
        </Modal>
    );
};

export default BankForm;
